import type { FraudContext } from '@src/quince-site-common/src/services/payment-service/payment-service.types'

declare global {
  interface Window {
    /** Forter device token mirrored by the snippet (also written to the `forterToken` cookie). */
    ftr__gt?: string
  }
}

const FORTER_COOKIE = 'forterToken'

/**
 * Reads the current Forter device token — the value of the `forterToken` cookie that the Forter snippet
 * (see forter-script.tsx) writes and also mirrors on `window.ftr__gt`.
 *
 * Returns undefined when the snippet hasn't produced a token yet (not loaded, blocked, or still
 * initialising). Callers MUST treat Forter as fail-open: a missing token must never block checkout.
 */
export function getForterDeviceToken(): string | undefined {
  if (typeof window === 'undefined') return undefined

  const fromWindow = window.ftr__gt
  if (typeof fromWindow === 'string' && fromWindow.length > 0) {
    return fromWindow
  }

  return readForterCookie()
}

/**
 * Builds the Forter fraud signals the FE supplies on the authorize call. Returns undefined when no Forter
 * token is available yet (callers treat Forter as fail-open — never block on a missing token).
 */
export function buildFraudContext(): FraudContext | undefined {
  const deviceToken = getForterDeviceToken()
  if (!deviceToken) return undefined
  return { deviceToken }
}

function readForterCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined
  // eslint-disable-next-line @quince/no-direct-cookie -- read the raw forterToken cookie exactly as the vendor snippet wrote it
  const entry = document.cookie.split('; ').find(row => row.startsWith(`${FORTER_COOKIE}=`))
  if (!entry) return undefined
  const value = decodeURIComponent(entry.slice(FORTER_COOKIE.length + 1))
  return value || undefined
}
